<template>
  <transition name="bounce">
    <div
      class="cookiebar"
      v-if="is_visible"
    >
      <div class="cookiebar__header">
        <h4>
          {{ t('literal.cookie_preferences') }}
        </h4>
      </div>

      <div class="padding__tb--18">
        <p v-if="showOverviewView">
          {{ t('literal.cookie_preferences_description') }}
        </p>

        <template v-if="showManageView">
          <div>
            <input-checkbox
              name="functional"
              v-model="consent.functional"
              :disabled="true"
            >
              <strong class="bold">{{ t('literal.functional_cookies') }}</strong>
            </input-checkbox>

            <p class="cookiebar__description color--vlg margin__t--6 padding__l--30">
              {{ t('literal.functional_cookies_description') }}
            </p>
          </div>

          <div class="margin__t--18">
            <input-checkbox
              name="analytics"
              v-model="consent.analytics"
            >
              <strong class="bold">{{ t('literal.analytics_cookies') }}</strong>
            </input-checkbox>

            <p class="cookiebar__description margin__t--6 padding__l--30">
              {{ t('literal.analytics_cookies_description') }}
            </p>
          </div>

          <div class="margin__t--18">
            <input-checkbox
              name="marketing"
              v-model="consent.marketing"
            >
              <strong class="bold">{{ t('literal.marketing_cookies') }}</strong>
            </input-checkbox>

            <p class="cookiebar__description margin__t--6 padding__l--30">
              {{ t('literal.marketing_cookies_description') }}
            </p>
          </div>
        </template>

        <router-link
          target="_blank"
          class="cookiebar__link link__color--white-prim link--naked margin__t--18"
          :to="{ name: 'Page', params: { path: 'cookie-policy', locale: $i18n.locale } }"
        >
          {{ t('literal.read_our_full_cookie_policy') }}

          <fa-icon
            class="margin__l--6"
            size="lg"
            :icon="['fal', 'external-link']"
            fixed-width
          />
        </router-link>
      </div>

      <div class="cookiebar__footer padding__t--18">
        <template v-if="showOverviewView">
          <button
            class="btn btn__size--small btn__color--prim"
            @click="acceptAll"
          >
            {{ t('literal.accept_cookies') }}
          </button>

          <button
            class="link__block link__color--white-prim margin__t--12"
            @click="switchView('manage')"
          >
            {{ t('literal.manage_cookies') }}
          </button>
        </template>

        <template v-if="showManageView">
          <button
            class="btn btn__size--small btn__color--prim"
            @click="setConsent"
          >
            {{ t('literal.save_preferences') }}
          </button>

          <button
            class="link__block link__color--white-prim margin__t--12"
            @click="acceptAll"
          >
            {{ t('literal.accept_all_cookies') }}
          </button>
        </template>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from '@/vendors/i18n'
import { useConsent } from '@/vendors/consent'
import { useGoogleTagManager } from '@/vendors/integrations'
import { $cookies } from '@/utils/utils'

import InputCheckbox from '&/atoms/InputCheckbox'

const { t } = useI18n()
const { subscribe, setPurposes } = useConsent()
const gtm = useGoogleTagManager()

const cookie = $cookies.get('koalect_consent') || []

const is_visible = ref(false)
const view = ref('manage')

const consent = ref({
  functional: true,
  analytics: cookie.includes('analytics'),
  marketing: cookie.includes('marketing')
})

const showOverviewView = computed(() => view.value === 'overview')
const showManageView = computed(() => view.value === 'manage')

const switchView = key => view.value = key
const close = () => is_visible.value = false

const acceptAll = () => {
  consent.value.analytics = true
  consent.value.marketing = true

  setConsent()
}

const setConsent = () => {
  $cookies.set({ name: 'koalect_consent', value: Object.entries(consent.value).filter(([, value]) => value).map(([type]) => type).join(' ') })

  view.value = 'manage'

  setPurposes(consent.value)
  setGtmConsent('update')
  close()
}

const setGtmConsent = type => {
  gtm.consent({ type, consent: {
    functionality_storage: consent.value.functional ? 'granted' : 'denied',
    security_storage: consent.value.functional ? 'granted' : 'denied',
    analytics_storage: consent.value.analytics ? 'granted' : 'denied',
    ad_storage: consent.value.marketing ? 'granted' : 'denied',
    ad_user_data: consent.value.marketing ? 'granted' : 'denied',
    ad_personalization: consent.value.marketing ? 'granted' : 'denied'
  } })
}

subscribe('cookieBarRequested', () => is_visible.value = true)

onMounted(() => {
  setGtmConsent()

  if (!cookie.length) {
    view.value = 'overview'

    setTimeout(() => is_visible.value = true, 1000)
  } else {
    setPurposes(consent.value)
  }
})
</script>

<style lang="scss" scoped>
.cookiebar {
  width: 300px;
  max-height: 90vh;
  overflow-y: auto;
  padding: $padding__base * 1.5;
  margin-left: -150px;
  border-radius: $radius__cards;
  word-break: break-word;
  background: $dg;
  color: $white;
  position: fixed;
  z-index: 999;
  left: 50%;
  bottom: 5vh;
  transform-origin: center;
  text-align: left;
  scrollbar-color: rgba(#fff, 0.1) transparent;
  scrollbar-width: thin;

  -webkit-overflow-scrolling: touch;

  @media screen and ( min-height: 520px ) {
    bottom: $margin__base * 2;
  }

  @include mq(sm) {
    left: 5vh;
    margin: 0;
  }

  &.bounce-enter-active {
    animation-name: bounce-in;
    animation-duration: 300ms;
    animation-timing-function: linear;
    animation-fill-mode: forwards;
  }

  &.bounce-leave-active {
    opacity: 0;
    transition: opacity .2s;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: em(20px);
  }

  &__footer {
    border-top: 1px solid rgba(#ffffff, 0.2);
    text-align: center;
  }

  &__description {
    font-size: 14px;
  }

  &__link {
    display: flex;
    align-items: center;
  }

  &:deep() .checkbox {
    &__label {
      color: $white;

      &.checkbox--disabled {
        color: $vlg;
      }
    }

    &__element {
      + .checkbox__icon {
        color: $color--primary;
      }

      &:focus + .checkbox__icon {
        color: $color--primary;
      }

      &:checked + .checkbox__icon {
        color: $color--success;
      }
    }
  }
}

.link {
  &__block {
    display: block;
    width: 100%;
  }
}
</style>
